/**
 * The Alert Creator's standing FEMA banner: where a message goes and which environment it goes
 * to, read from the server (`GET /fusion/v2/fema/meta`), never from a build-time constant
 * (card FE-01). Pure: the component passes the query state in and prints what comes out.
 * Multi-COG contract §2: when the meta serves `environments[]`, the banner describes the CHOSEN
 * class, not the first tenant of the service.
 */
import {
  getDefaultFemaEnvironmentChoice,
  getFemaMetaEnvironmentEntry,
  readFemaMetaEnvironments,
  resolveFemaEnvironmentClass,
} from './femaEnvironment.utils';

/** RescAlert: the three environment classes the service can run as (fema.types.ts). */
export const FEMA_ENVIRONMENT_LABELS = {
  TDL: 'Test Development Lab (TDL) / Staging',
  DEMO: 'IPAWS DEMO / TEST',
  PRODUCTION: 'IPAWS LIVE',
};

const disseminationExplanation = (environment) =>
  environment === 'PRODUCTION'
    ? 'Messages are sent to FEMA’s production IPAWS-OPEN environment and will be disseminated to the public through the selected and authorized channels.'
    : environment === 'DEMO'
      ? 'Messages are sent to the configured IPAWS demonstration or test destination and do not alert the public.'
      : 'Messages are sent to FEMA’s IPAWS-OPEN Test Development Lab and do not alert the public.';

const maskedCogId = (environment, cogId) => {
  if (!cogId) return 'Not provided';
  if (environment === 'TDL') return '1*****';
  if (environment === 'DEMO') return '3XXXXXX';
  if (environment === 'PRODUCTION') return '2XXXXXX';
  return 'Not provided';
};

const reasonOf = (error) => {
  // axiosBaseQuery hands RTK { status, message, raw: <the body> }; { status, data } is read too.
  const data = error?.raw ?? error?.data;
  const message = data?.message ?? error?.message ?? data?.service?.message ?? data?.error;
  if (typeof message === 'string' && message.trim()) return message.trim();
  if (Number.isFinite(Number(error?.status))) return `HTTP ${error.status}`;
  return 'No answer from the server';
};

/** The date part of an ISO time, or `null`. */
const datePartOf = (value) => {
  if (typeof value !== 'string') return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : new Date(time).toISOString().slice(0, 10);
};

/**
 * FE-11 acceptance: an expired certificate reads as an outage.
 * @param {string|null} certificateNotAfter
 */
export const describeExpiredCertificate = (certificateNotAfter) => {
  const date = datePartOf(certificateNotAfter);
  return date
    ? `The FEMA certificate expired on ${date}. IPAWS alerts cannot be sent.`
    : 'The FEMA certificate expired. IPAWS alerts cannot be sent.';
};

const endSentence = (text) => (/[.!?]$/.test(text) ? text : `${text}.`);

/**
 * The credential sentence for one held class (multi-COG contract §2 and §4).
 * @param {import('types/fema.types').FemaMetaEnvironment} entry
 */
const describeEntryCredential = (entry) => {
  if (entry.credentialState === 'expired')
    return describeExpiredCertificate(entry.certificateNotAfter);
  if (entry.canSend) {
    return entry.credentialState === 'expiring' && Number.isInteger(entry.daysToExpiry)
      ? `Credential loaded. The FEMA certificate expires in ${entry.daysToExpiry} days.`
      : 'Credential loaded.';
  }
  return endSentence(entry.reason ?? 'This environment cannot send; a send will be refused');
};

/**
 * The banner for the multi-COG meta: it describes the chosen class (contract §2).
 * @param {object} meta The `/meta` answer with an `environments` array.
 * @param {string|null|undefined} choice The class token in the draft.
 */
const describeChosenClass = (meta, choice) => {
  // The one class reader; before the select stores a class, the default (never production).
  const chosen = resolveFemaEnvironmentClass(choice, meta) ?? getDefaultFemaEnvironmentChoice(meta);
  const environment = String(chosen ?? '').toUpperCase();
  const label = FEMA_ENVIRONMENT_LABELS[environment] ?? (environment || 'not selected');
  const entry = getFemaMetaEnvironmentEntry(meta, chosen);
  const destination = meta.destination ?? 'IPAWS-OPEN';

  if (!entry) {
    return {
      tone: 'ready',
      destinationName: label,
      environment: `Environment: ${label}`,
      detail: chosen
        ? `This workplace holds no FEMA COG for ${label}. A send will be refused.`
        : 'Select a FEMA environment.',
      issuingAuthority: null,
      cogId: null,
      cogName: null,
      cogDisplayId: 'Not provided',
      credentialStatus: 'Not Ready',
      dissemination: disseminationExplanation(environment),
    };
  }

  const isExpired = entry.credentialState === 'expired';
  // Final polish (`085af8d2e`): one line per fact. A class that can send with no expiry note
  // needs no detail line; the COG shows masked.
  const credential = describeEntryCredential(entry);
  const hasCredentialNote = !entry.canSend || entry.credentialState === 'expiring';
  return {
    tone: isExpired ? 'unavailable' : 'ready',
    destinationName: label,
    environment: `Environment: ${label}`,
    detail: hasCredentialNote
      ? `Messages go to FEMA's ${destination} ${environment}. ${credential}`
      : '',
    issuingAuthority: entry.organizationName ?? null,
    cogId: entry.cogId,
    cogName: entry.cogName ?? null,
    cogDisplayId: maskedCogId(environment, entry.cogId),
    credentialStatus: entry.canSend ? 'Ready' : isExpired ? 'Expired' : 'Not Ready',
    dissemination: disseminationExplanation(entry.isProduction ? 'PRODUCTION' : environment),
  };
};

/**
 * @param {{ isLoading?: boolean, isError?: boolean, error?: object, data?: object }} query
 * @param {string|null} [choice] The class token in the draft (`tdl`, `demo`, `production`). Read
 *   only when the meta serves `environments[]`; an older meta describes the served class.
 * @returns {{ tone: 'checking'|'unavailable'|'ready', environment: string, detail: string }}
 */
export const describeFemaDestination = (query = {}, choice = null) => {
  if (query.isError) {
    return {
      tone: 'unavailable',
      destinationName: 'FEMA IPAWS public warning destination',
      environment: 'Environment: FEMA service not reachable',
      detail: `${reasonOf(query.error)}. Submission is unavailable until the service answers.`,
      issuingAuthority: null,
      cogId: null,
      cogName: null,
      cogDisplayId: 'Not provided',
      credentialStatus: 'Unavailable',
      dissemination:
        'The public-warning destination cannot be confirmed until the service answers.',
    };
  }

  const meta = query.data;
  if (query.isLoading || !meta) {
    return {
      tone: 'checking',
      destinationName: 'FEMA IPAWS public warning destination',
      environment: 'Environment: checking the FEMA service…',
      detail: 'Submission waits for the service to answer.',
      issuingAuthority: null,
      cogId: null,
      cogName: null,
      cogDisplayId: 'Not provided',
      credentialStatus: 'Checking',
      dissemination: 'The public-warning destination is being confirmed.',
    };
  }

  if (readFemaMetaEnvironments(meta) !== null) return describeChosenClass(meta, choice);

  const environment = String(meta.environment ?? '').toUpperCase();
  const label = FEMA_ENVIRONMENT_LABELS[environment] ?? environment ?? 'unknown';
  const tenant = Array.isArray(meta.tenants) ? meta.tenants[0] : undefined;
  const credential = !tenant
    ? 'No tenant is registered for this environment; a send will be refused.'
    : tenant.credentialLoaded
      ? 'Credential loaded.'
      : 'No credential loaded; a send will be refused.';

  return {
    tone: 'ready',
    destinationName: label,
    environment: `Environment: ${label}`,
    detail: tenant?.credentialLoaded ? '' : credential,
    issuingAuthority: tenant?.organizationName ?? null,
    cogId: tenant?.cogId ?? null,
    cogName: tenant?.cogName ?? null,
    cogDisplayId: maskedCogId(environment, tenant?.cogId),
    credentialStatus: tenant?.credentialLoaded ? 'Ready' : 'Not Ready',
    dissemination: disseminationExplanation(environment),
  };
};
